<template>
    <div>
        <div class="mb-4 md:mb-8">
            <div class="px-4 py-3 grid grid-flow-col gap-2 items-center relative overflow-hidden users-container">
                <label 
                    class="cursor-pointer" 
                    v-for="org in myOrgs" 
                    :key="org._id"
                    @click.stop="selectOrganization(org)"
                >
                    <span>{{ org.name }}</span>
                </label>
            </div>
        </div>
        <div class="">
            <div id="organisationChartBody" class="organisationChartBody read-only-chart" ref="organiGramBox">
            </div>
        </div>

        <!-- Hidden component for the Infographic Profile -->
        <talent-card-preview 
            ref="otherUserCard"
            :userId="selectedOtherUserId" 
            :initialData="selectedOtherUserData"
        />
    </div>
</template>

<script>
import constants from "@/constants";
import { OrganiGram as OrganiGramChart } from "../../../org-dashboard/users/components/OrganiGram/src/organi-gram";
import { app } from "../../../org-dashboard/users/components/OrganiGram/src/feathers-socketio";
import { kvdrOrgConfig } from "@/assets/data/kvdrOrgConfigs.js";
import skills from "@/assets/data/all-skills.js";
import softSkills from "@/assets/data/all-personality.js";

import TalentCardPreview from "@/views/org-dashboard/talent-pool/components/TalentCardPreview.vue";

export default {
    props: {
        'importData': Array
    },
    name: 'OrganiGram',
    components: {
        TalentCardPreview 
    },
    data() {
        return {
            chartObject: null,
            candidateList: null,
            dataCollection: '',
            kvdrId: '',
            myOrgs: [],
            orgData: {},
            userRole: '1',
            orgEmployees: [],
            accessLevel: [],
            isUserView: false,
            selectedOtherUserId: '',
            selectedOtherUserData: {},
        }
    },
    computed: {
        userId() {
            return this.$store.state.auth.user._id;
        },
        userData() {
            return this.$store.state.users.data;
        },
        employeesData() {
            return this.$store.state.organization.employees;
        },
    },
    methods: {
        renderChart(data) {
            const safeId = (val) => (val ? String(val) : null);
            const currentUserId = safeId(this.userId);
            
            this.isUserView = (this.userRole === '0' || this.userRole === 0 || this.userRole === '1' || this.userRole === 1);
            
            this.chartObject = new OrganiGramChart('abc123def456', 'KVDR');
            this.chartObject.container('organisationChartBody')
                .id(this.kvdrId)
                .root({
                    id: this.orgData._id,
                    name: this.orgData.name,
                    image: constants.s3Bucket + '/organization/logo/' + this.orgData.logo
                })
                .type('F')
                .data(data)
                .role(0)
                .candidateList(this.candidateList)
                .user(this.userData)
                .skills(skills)
                .softSkills(softSkills)
                .displaySettings({
                    cardColor: this.orgData.primaryThemeColor,
                    textColor: '#' + this.orgData.secondaryThemeColor,
                    borderColor: 'rgba(250,250,0,0.1)'
                })
                .onNodeClick((d) => { return d; }) // Reset to default
                .onNodeFetch(d => {
                    if (this.isUserView) {
                        const isUserInNode = d.kvdrRoleProfiles && d.kvdrRoleProfiles.some(p => safeId(p.userId) === currentUserId);
                        if (!isUserInNode) {
                            return Promise.resolve({
                                _id: d._id || '', title: d.title || d.name || 'Role', name: d.name || d.title || 'Role',
                                kvdrRoleProfiles: [], functionalSkills: [], behavioralSkills: [],
                            });
                        }
                    }
                    return this.$store.dispatch('kvdr/findKvdrRole', d).then(res => ({...res.data, ...res}));
                })
                .onNodeProfileFetch(d => {
                    if (this.isUserView) {
                        let requestUserId = null;
                        if (d.userId) requestUserId = safeId(d.userId);
                        else if (typeof d === 'string' && d.includes('userId=')) {
                            const match = d.match(/userId=([^&]+)/);
                            if (match) requestUserId = match[1];
                        }
                        if (!requestUserId || requestUserId !== currentUserId) return Promise.resolve([]);
                    }
                    return this.$store.dispatch('kvdr/findKvdrRoleProfile', d).then(res => res.data || []).catch(() => []);
                })
                .onNodeProfileUpdate(d => {
                    return this.$store.dispatch('kvdr/patchKvdrRoleProfile', d).then(res => {
                            this.$vs.notify({ title: 'Profile Update', text: 'Profile updated successfully', color: '#008000' });
                            return Array.isArray(res) ? res[0] : res;
                        }).catch(err => {
                            this.$vs.notify({ title: 'Error', text: 'Failed to update profile', color: 'danger' });
                            throw err;
                        });
                })
                .render();

            // ====================================================================
            // THE CRITICAL FIX: INTERCEPT THE LIBRARY'S INTERNAL POPUP FUNCTION
            // ====================================================================
            if (this.chartObject && typeof this.chartObject.expandRole === 'function') {
                const originalExpandRole = this.chartObject.expandRole; // Save the original function

                this.chartObject.expandRole = (data, type, id) => {
                    
                    // If we are in user mode (not admin)
                    if (this.isUserView) {
                        let targetUserId = null;

                        // When clicking an avatar, the library passes type="Talent" and id=["userId"]
                        if (type === 'Talent' && id && id.length > 0) {
                            targetUserId = String(id[0]);
                        } 
                        // When clicking the edit icon on the node, we must extract the ID manually
                        else if (type === 'ROLE' && data && data.kvdrRoleId) {
                            const node = document.querySelector(`[data-empid='${data.kvdrRoleId}']`);
                            if (node && node.dataset.selectedUsr) {
                                const users = node.dataset.selectedUsr.split(",").filter(Boolean);
                                if (users.length > 0) targetUserId = String(users[0]);
                            }
                        }

                        // 1. IS IT SOMEONE ELSE? -> Show Infographic
                        if (targetUserId && targetUserId !== currentUserId) {
                            const chartUser = (this.candidateList || []).find(c => String(c.id) === targetUserId) || {};
                            
                            this.openInfographicProfile(targetUserId, chartUser);
                            
                            // 🔥 RETURN HERE halts execution so the library's popup NEVER opens
                            return; 
                        }
                    }
                    
                    // 2. IS IT ME (or Admin view)? -> Run the original library function
                    return originalExpandRole.call(this.chartObject, data, type, id);
                };
            }
            // ====================================================================
        },

        openInfographicProfile(userId, userData) {
            this.selectedOtherUserId = userId;
            this.selectedOtherUserData = userData;
            
            this.$nextTick(() => {
                if (this.$refs.otherUserCard) {
                    this.$refs.otherUserCard.openTalentCard();
                }
            });
        },

        // ... (Keep createData, setupCandidateList, setKVDRState, fetchOrgs, etc exactly as they were) ...
        createData(chartData) {
            let allRoles = chartData.kvdrRoles || [];
            const safeId = (val) => (val ? String(val) : null);
            const currentUserId = safeId(this.userId);

            if (this.isUserView) {
                const userNodes = allRoles.filter(role => role.kvdrRoleProfiles && role.kvdrRoleProfiles.some(p => safeId(p.userId) === currentUserId));

                if (userNodes.length > 0) {
                    const visibleNodeIds = new Set();
                    const getNodeId = (node) => safeId(node.selfId || node._id);
                    const getParentId = (node) => safeId(node.parentId);

                    const getAncestors = (nodeId) => {
                        if (!nodeId) return;
                        visibleNodeIds.add(nodeId); 
                        const currentNode = allRoles.find(r => getNodeId(r) === nodeId);
                        if (currentNode) {
                            const pId = getParentId(currentNode);
                            if (pId && !visibleNodeIds.has(pId) && pId !== "0") getAncestors(pId);
                        }
                    };

                    const getDescendants = (nodeId) => {
                        if (!nodeId) return;
                        visibleNodeIds.add(nodeId);
                        const children = allRoles.filter(r => getParentId(r) === nodeId);
                        children.forEach(child => {
                            const childId = getNodeId(child);
                            if (childId && !visibleNodeIds.has(childId)) getDescendants(childId);
                        });
                    };

                    userNodes.forEach(uNode => {
                        const uId = getNodeId(uNode);
                        if (uId) { getAncestors(uId); getDescendants(uId); }
                    });

                    allRoles = allRoles.filter(role => visibleNodeIds.has(getNodeId(role)));
                } else {
                    allRoles = [];
                }
            }

            const padTitle = (title) => {
                const minLength = 40; 
                if (!title) return "";
                let str = String(title);
                if (str.length >= minLength) return str;
                const diff = minLength - str.length;
                const leftPad = Math.floor(diff / 2);
                const rightPad = Math.ceil(diff / 2);
                return '\u2003'.repeat(leftPad) + str + '\u2003'.repeat(rightPad);
            };

            const graphData = allRoles.map(role => {
                let modifiedRole = { ...role };
                modifiedRole.name = padTitle(role.title); 
                modifiedRole.description = role.desc || '';
                modifiedRole.profiles = (role.kvdrRoleProfiles || []).map(p => {
                    let assessmentData = { 'Fitment (Fit)': [], 'Performance (Drive)': [], 'Succession (Secure)': [], 'Appraisal': [] };
                    const isCurrentUser = safeId(p.userId) === currentUserId;
                    
                    if (!this.isUserView || isCurrentUser) {
                        assessmentData = {
                            'Fitment (Fit)': p.fitmentData || [],
                            'Performance (Drive)': p.performanceData || [],
                            'Succession (Secure)': p.successionData || [],
                            'Appraisal': p.appraisalData || []
                        };
                    }
                    return { userId: p.userId, assessmentData: assessmentData };
                });
                return modifiedRole;
            });

            return {
                id: chartData._id, 
                name: chartData.title, 
                version: chartData.version || "",
                kvdrOrgConfigData: kvdrOrgConfig(this.orgData._id), 
                graphData: graphData
            };
        },
        setupCandidateList() {
            if (this.orgEmployees.length) {
                this.candidateList = this.employeesData.map(m =>
                    ({
                        id: m.userId, name: m.userData.firstName + ' ' + m.userData.lastName,
                        imageUrl: (m.userData.profilePictureUrl) ? `${constants.s3Bucket}/editor/profile-pictures/${m.userData.profilePictureUrl}` : `https://hostedboringavatars.vercel.app/api/beam?name=${m.userData.firstName}`,
                        emailId: m.userData.email,
                    }))
            } else {
                this.candidateList = [{
                    id: this.userId, name: this.userData.firstName + ' ' + this.userData.lastName,
                    imageUrl: (this.userData.profilePictureUrl) ? `${constants.s3Bucket}/editor/profile-pictures/${this.userData.profilePictureUrl}` : `https://hostedboringavatars.vercel.app/api/beam?name=${this.userData.firstName}`,
                    emailId: this.userData.email,
                }]
            }
        },
        setKVDRState(_id) {
            this.fetchOrgEmps(_id);
            this.$store.dispatch("kvdr/findKVDR", _id).then(res => {
                    this.kvdrId = res.data[0]._id;
                    this.setupCandidateList();
                    this.dataCollection = this.createData(res.data[0]);
                    this.renderChart(this.dataCollection);
                }).catch((err) => { console.error('Error setting KVDR state:', err); throw err; });
        },
        async fetchOrgs() {
            const myOrganizations = await this.$store.dispatch("organization/fetchOrganizationByUserId", this.userId);
            const orgList = myOrganizations.data.data || [];
            return orgList.filter(org => {
                if (!org.status) return true; 
                return String(org.status).toUpperCase() !== 'DEACTIVATED';
            });
        },
        async fetchOrgEmps(org) {
            return this.$store.dispatch("organization/fetchAllEmployees", { organizationId: org })
                .then(employees => {
                    this.orgEmployees = employees;
                    const userData = employees.filter(employee => this.userId === employee.userId);
                    if (userData.length) {
                        this.userRole = this.getAccessLevelKey(userData[0].accessLevel) || 1;
                        this.isUserView = (this.userRole === 0 || this.userRole === '0' || this.userRole === 1 || this.userRole === '1');
                    }
                    return true;
                }).catch(err => { console.error('Error fetching employees:', err); return err; })
        },
        selectOrganization(org) {
            this.orgData = org;
            this.accessLevel = kvdrOrgConfig(org._id).accessLevel;
            this.fetchOrgEmps(org._id).then(res => { res === true ? this.setKVDRState(org._id) : "" });
        },
        getAccessLevelKey(label) {
            return this.accessLevel.filter(item => item.label === label).map(item => item.key)[0] || 0
        }
    },
    mounted() { this.fetchOrgs().then(myOrgs => this.myOrgs = myOrgs); },
    created() { this.fetchOrgs().then(myOrgs => this.myOrgs = myOrgs); }
}
</script>

<style scoped>
/* (Leave all your styles completely untouched here) */
#organisationChartBody { border-top: 1px solid #f1f5f9; }
.users-container { background: #fff; box-shadow: 0px 4px 36px rgba(24, 144, 251, 0.1); border-radius: 0.5rem; flex-wrap: wrap; }
::v-deep .node rect { rx: 5px; ry: 5px; }
::v-deep image { object-fit: cover !important; }
::v-deep .node foreignObject { overflow: visible; }
::v-deep .node foreignObject > div { min-width: 280px !important; width: 280px !important; min-height: 90px !important; display: flex !important; justify-content: center; align-items: center; box-sizing: border-box; }
::v-deep text { white-space: pre !important; }
::v-deep .node-add-btn, ::v-deep .org-add-button, ::v-deep g[style*="cursor: pointer"] circle, ::v-deep .node-button-g { display: none !important; pointer-events: none !important; opacity: 0 !important; }
::v-deep .read-only-chart .member { cursor: pointer !important; }
@import '../../../org-dashboard/users/components/OrganiGram/public/css/app.css';
</style>